import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";
import RecordAMLAfterMLCOClear from "./RecordAMLAfterMLCOClear";

/**
 * R&D-E Gap 4 — MLRO Alerts panel.
 *
 * Shows unresolved MLRO alerts plus anything resolved in the last 90
 * days, with acknowledge / resolve actions. Auth is enforced server-side
 * (MLRO / director / COLP / admin); non-eligible users see an error.
 */
const STATUS_STYLE = {
  open: "text-red-300",
  acknowledged: "text-amber-300",
  resolved: "text-slate-400",
};

export default function MLROAlertsPanel({ authHeaders }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actingId, setActingId] = useState(null);
  // BM-H3733: the matter whose referred-Thirdfort alert was just resolved.
  const [amlPromptRef, setAmlPromptRef] = useState(null);

  const headers = useCallback(() => {
    if (typeof authHeaders === "function") return authHeaders();
    const email =
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") ||
      "";
    return { "X-Bonsai-User-Id": email };
  }, [authHeaders]);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/compliance/mlro-alerts`, {
        headers: headers(),
      });
      const rows = Array.isArray(res.data?.alerts) ? res.data.alerts : [];
      setAlerts(rows);
    } catch (err) {
      setError(errorMessage(err, "request failed"));
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const act = useCallback(
    async (alert, action) => {
      const alertId = alert.id;
      setActingId(alertId);
      setError(null);
      try {
        await axios.post(
          `${API}/compliance/mlro-alerts/${alertId}/${action}`,
          {},
          { headers: headers() }
        );
        if (action === "resolve" && alert.event_type === "thirdfort_referred" && alert.matter_ref) {
          setAmlPromptRef(alert.matter_ref);
        }
        await fetchAlerts();
      } catch (err) {
        setError(errorMessage(err, `${action} failed`));
      } finally {
        setActingId(null);
      }
    },
    [headers, fetchAlerts]
  );

  const unresolved = alerts.filter(
    (a) => a.status === "open" || a.status === "acknowledged"
  ).length;

  return (
    <section
      data-testid="mlro-alerts-panel"
      className="rounded-xl border border-white/[0.07] bg-navy-card p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-100">
          MLRO Alerts{" "}
          {unresolved > 0 && (
            <span
              data-testid="mlro-alerts-badge"
              className="ml-1 inline-flex items-center justify-center rounded-full bg-red-900/60 text-red-200 text-[10px] font-semibold px-1.5 py-0.5"
            >
              {unresolved}
            </span>
          )}
        </h2>
        <button
          type="button"
          onClick={fetchAlerts}
          disabled={loading}
          data-testid="mlro-alerts-refresh"
          className="px-2.5 py-1 rounded text-xs font-medium border bg-slate-700 text-slate-100 border-slate-600 hover:bg-slate-600 disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <p className="text-xs text-slate-400">
        SRA-material MLRO notifications (AML Policy v3 §6/7/8). Unresolved
        alerts plus anything resolved in the last 90 days.
      </p>

      {amlPromptRef && (
        <RecordAMLAfterMLCOClear
          key={amlPromptRef}
          matterRef={amlPromptRef}
          headers={headers}
          onDismiss={() => setAmlPromptRef(null)}
        />
      )}

      {error ? (
        <div className="rounded-md border border-red-800/50 bg-red-950/30 p-3 text-sm text-red-200">
          <span data-testid="mlro-alerts-error">{error}</span>{" "}
          <button
            type="button"
            onClick={fetchAlerts}
            className="ml-2 px-2 py-0.5 rounded border border-red-700/60 hover:bg-red-900/30"
          >
            Retry
          </button>
        </div>
      ) : loading && alerts.length === 0 ? (
        <div
          data-testid="mlro-alerts-skeleton"
          className="h-4 w-44 bg-slate-800 rounded animate-pulse"
        />
      ) : alerts.length === 0 ? (
        <div className="text-sm text-slate-400" data-testid="mlro-alerts-empty">
          No MLRO alerts.
        </div>
      ) : (
        <table className="w-full text-sm text-slate-200" data-testid="mlro-alerts-table">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-400">
              <th className="text-left py-1 pr-3">Event</th>
              <th className="text-left py-1 pr-3">Matter</th>
              <th className="text-left py-1 pr-3">Severity</th>
              <th className="text-left py-1 pr-3">Status</th>
              <th className="text-right py-1">Actions</th>
            </tr>
          </thead>
          <tbody>
            {alerts.map((a) => (
              <tr
                key={a.id}
                data-testid="mlro-alerts-row"
                className="border-t border-slate-800/60"
              >
                <td className="py-1 pr-3">{a.event_type}</td>
                <td className="py-1 pr-3 font-mono text-xs">{a.matter_ref || "—"}</td>
                <td className="py-1 pr-3 capitalize">{a.severity || "high"}</td>
                <td className={"py-1 pr-3 capitalize " + (STATUS_STYLE[a.status] || "")}>
                  {a.status}
                </td>
                <td className="py-1 text-right space-x-1">
                  {a.status === "open" && (
                    <button
                      type="button"
                      onClick={() => act(a, "acknowledge")}
                      disabled={actingId === a.id}
                      data-testid="mlro-alerts-acknowledge"
                      className="px-2 py-0.5 rounded text-xs border border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700 disabled:opacity-50"
                    >
                      Ack
                    </button>
                  )}
                  {(a.status === "open" || a.status === "acknowledged") && (
                    <button
                      type="button"
                      onClick={() => act(a, "resolve")}
                      disabled={actingId === a.id}
                      data-testid="mlro-alerts-resolve"
                      className="px-2 py-0.5 rounded text-xs border border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700 disabled:opacity-50"
                    >
                      Resolve
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
